import React, { createContext, useState, useContext, useRef, useEffect, useMemo } from "react";
import { useNavigate, useLocation } from 'react-router-dom';
import { checkAndInsertData } from "../db/users/checkAndInsertData";
import { loadInitialData } from "../db/loadInitialData";
import infosUser from "../db/users/InfosUser";
import { getUserChannels } from "../db/groupes/getUserChannels";
import { getUserProducts, getProductById } from "../db/produits/getUserProducts";
import CustomPaymentConfig from "../composant/espace/CustomPaymentConfig";
import { AggregatorManager } from "../db/Aggregator/AggregatorManager";
import { UserKeysService } from "../db/Aggregator/UserKeysService";
import { getAbonnementsByVendeur } from "../db/abonnement/getAbonnementsByVendeur";
import { database, ref, onValue, off, set, update } from "../FirebaseConfig";
import { updateVendeurInfo } from "../db/users/updateVendeurInfo";
// Utiliser l'API Telegram directement
import { useCallback } from 'react';

const AppContext = createContext();

// Routes et configurations statiques
const PURCHASE_ROUTES = ['/achats', '/paiement', '/checkout', '/payment-success', '/payment-error'];
const BYPASS_LOADING_ROUTES = ['/contact', '/PaymentStatus'];

export const AppProvider = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  // Référence à la navigtion pour pouvoir y accéder dans des setTimeout
  const [isSplashVisible, setSplashVisible] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [splashError, setSplashError] = useState(null);
  const [ontg, setOnTg] = useState(null);
  const [id, setId] = useState(null);
  const [showPaymentConfig, setShowPaymentConfig] = useState(false);
  const [showPaymentSheet, setShowPaymentSheet] = useState(false);
  const [userInfos, setUserInfos] = useState(null);
  const [userProducts, setUserProducts] = useState([]);
  const [channels, setChannels] = useState(null);
  const [unProduit, setUnProduit] = useState(null);
  const [allAggregators, setAllAggregators] = useState(null);
  const [userActiveAggregators, setUserActiveAggregators] = useState(null);
  const [adminActiveAggregators, setAdminActiveAggregators] = useState(null);
  const [abonnementProduit, setAbonnementProduit] = useState(null);
  const [method, setMethod] = useState(null);
  
  // Refs pour contrôle d'état interne
  const isInitialized = useRef(false);
  const isUpdating = useRef(false);
  const dataLoaded = useRef(false);
  const currentPath = useRef(location.pathname);
  const redirectDone = useRef(false);
  const lastUpdate = useRef(Date.now());
  
  // Fonction pour charger ou recharger les données utilisateur (lors des changements de route)
  const loadUserData = async (userId) => {
    // Ne pas recharger si déjà en cours de mise à jour ou sur une route à ignorer
    if (isUpdating.current || BYPASS_LOADING_ROUTES.includes(location.pathname)) {
      return;
    }
    
    // Éviter les mises à jour trop fréquentes (limitation de débit)
    const now = Date.now();
    if (now - lastUpdate.current < 500) { // 500ms minimum entre les updates
      return;
    }
    lastUpdate.current = now;
    
    try {
      isUpdating.current = true;
      setIsLoading(true);
      
      // Si on est sur une route d'achat, charger uniquement les données du produit
      if (PURCHASE_ROUTES.some(route => location.pathname.startsWith(route))) {
        const searchParams = new URLSearchParams(location.search);
        const idVendeur = searchParams.get('idVendeur');
        const idProduit = searchParams.get('idProduit');
        
        if (idVendeur && idProduit) {
          const produit = await getProductById(idVendeur, idProduit);
          setUnProduit(produit);
        }
      } 
      // Sinon, recharger toutes les données via loadInitialData
      else {
        try {
          const refreshedData = await loadInitialData(userId);
          
          if (refreshedData.success) {
            // Mettre à jour tous les états avec les données récupérées
            if (refreshedData.data.userData) {
              setUserInfos(refreshedData.data.userData);
            }
            
            if (refreshedData.data.userChannels) {
              setChannels(refreshedData.data.userChannels);
            }
            
            if (refreshedData.data.userProducts) {
              setUserProducts(refreshedData.data.userProducts);
            }
            
            if (refreshedData.data.allAggregators) {
              setAllAggregators(refreshedData.data.allAggregators);
            }
            
            if (refreshedData.data.userAggregators) {
              setUserActiveAggregators(refreshedData.data.userAggregators);
            }
            
            if (refreshedData.data.adminAggregators) {
              setAdminActiveAggregators(refreshedData.data.adminAggregators);
            }
            
            if (refreshedData.data.userSubscriptions.length > 0) {
              setAbonnementProduit(refreshedData.data.userSubscriptions);
            }
            
            // Désactivé temporairement pour éviter les erreurs de constructeur
            console.log("Payment configuration check skipped during data refresh");
          }
        } catch (error) {
          console.error("Erreur lors du rechargement des données:", error);
        }
      }
      
      dataLoaded.current = true;
    } catch (error) {
      console.error("Erreur lors du chargement des données:", error);
    } finally {
      setIsLoading(false);
      isUpdating.current = false;
    }
  };

  // Fonction pour écouter les changements de canaux en temps réel
  const setupChannelsListener = (userId) => {
    // Créer une référence à la localisation des canaux dans Firebase
    const channelsRef = ref(database, `channels/${userId}`);
    
    // Attacher un écouteur d'événements
    const unsubscribe = onValue(channelsRef, (snapshot) => {
      if (snapshot.exists()) {
        const channelsData = snapshot.val();
        setChannels(channelsData);
        console.log("Canaux mis à jour (listener):", channelsData);
      } else {
        setChannels({});
      }
    });
    
    // Retourner une fonction pour désactiver l'écouteur
    return () => unsubscribe();
  };

  // Initialisation de l'application
  useEffect(() => {
    // Ne s'exécute qu'une seule fois
    if (isInitialized.current) return;
    isInitialized.current = true;
    
    // Laisser le splash screen visible 
    setSplashVisible(true);
    
    // Définir un timeout pour masquer le splash screen si l'initialisation prend trop de temps
    const splashTimeout = setTimeout(() => {
      console.log("Timeout du splash screen atteint - masquage forcé");
      setSplashVisible(false);
    }, 3000); // 3 secondes maximum
    
    const init = async () => {
      try {
        setIsLoading(true);
        
        // 1. Détection Telegram - LOGIQUE SIMPLIFIÉE ET STRICTE
        let user;
        
        try {
          // Fonction pour extraire les données du hash Telegram (comme dans kappela-clien-web)
          const extractTelegramHashData = () => {
            const hash = window.location.hash;
            if (!hash || !hash.includes('tgWebAppData=')) return null;

            try {
              // Récupérer la partie tgWebAppData
              const tgDataMatch = hash.match(/tgWebAppData=([^&]+)/);
              if (!tgDataMatch || !tgDataMatch[1]) return null;
              
              const tgData = tgDataMatch[1];
              
              // Décodage
              const decodedStr = decodeURIComponent(tgData);
              
              // Création d'un objet à partir des paires clé=valeur
              const result = {};
              const pairs = decodedStr.split('&');
              
              for (const pair of pairs) {
                const [key, rawValue] = pair.split('=');
                if (!key || !rawValue) continue;
                
                // Décodage spécial pour 'user' qui est un objet JSON
                if (key === 'user') {
                  try {
                    const jsonStr = decodeURIComponent(rawValue);
                    result[key] = JSON.parse(jsonStr);
                  } catch (e) {
                    console.error('Erreur lors du décodage de user:', e);
                    result[key] = rawValue;
                  }
                } else {
                  result[key] = rawValue;
                }
              }
              
              // Récupérer les autres paramètres
              const versionMatch = hash.match(/tgWebAppVersion=([^&]+)/);
              if (versionMatch && versionMatch[1]) {
                result.version = decodeURIComponent(versionMatch[1]);
              }
              
              return result;
            } catch (error) {
              console.error('Erreur lors du décodage du hash:', error);
              return null;
            }
          };
          
          // Fonction pour configurer Telegram WebApp manuellement
          const setupTelegramWebApp = (hashData) => {
            if (!hashData) return false;
            
            try {
              // Créer l'objet Telegram s'il n'existe pas
              if (!window.Telegram) {
                window.Telegram = {};
              }
              
              // Créer ou compléter l'objet WebApp
              window.Telegram.WebApp = {
                ...(window.Telegram.WebApp || {}),
                initData: window.location.hash.substring(1),
                initDataUnsafe: {
                  ...(window.Telegram.WebApp?.initDataUnsafe || {}),
                  user: hashData.user || {},
                  query_id: hashData.query_id || "",
                  auth_date: hashData.auth_date || "",
                  hash: hashData.hash || ""
                },
                version: hashData.version || "6.0",
                platform: "web",
                colorScheme: "light",
                
                // Fonctions nécessaires si elles n'existent pas déjà
                ready: window.Telegram.WebApp?.ready || (() => console.log("WebApp ready called")),
                expand: window.Telegram.WebApp?.expand || (() => console.log("WebApp expand called")),
                setHeaderColor: window.Telegram.WebApp?.setHeaderColor || (() => {})
              };
              
              return true;
            } catch (error) {
              console.error("Erreur lors de la configuration de Telegram WebApp:", error);
              return false;
            }
          };
          
          // Simuler un utilisateur Telegram pour le développement
          const mockTelegramUser = {
            user: {
              id: 123456789,
              first_name: "Test",
              username: "test_user",
              language_code: "fr"
            },
            auth_date: Math.floor(Date.now() / 1000),
            hash: "dummy_hash_for_testing",
            version: "6.0"
          };
          
          // 0. Vérifier si des données sont dans le hash Telegram
          const hashData = extractTelegramHashData();
          if (hashData) {
            console.log("Données Telegram trouvées dans le hash!");
            const setupSuccess = setupTelegramWebApp(hashData);
            if (setupSuccess) {
              console.log("Objet Telegram.WebApp configuré à partir des données du hash");
            }
          }
          
          // Vérifier si l'API Telegram est déjà disponible
          let telegramApiAvailable = Boolean(window.Telegram && window.Telegram.WebApp);
          
          // Si l'API n'est pas disponible, essayer de la configurer manuellement
          if (!telegramApiAvailable) {
            console.log("API Telegram non détectée, configuration manuelle...");
            const setupSuccess = setupTelegramWebApp(mockTelegramUser);
            telegramApiAvailable = setupSuccess;
          }
          
          console.log("RÉSULTAT DÉTECTION TELEGRAM:", telegramApiAvailable ? "DANS_TELEGRAM ✓" : "HORS_TELEGRAM ✗");
          console.log("WebApp disponible:", Boolean(window.Telegram && window.Telegram.WebApp));
          
          // Définir l'état en fonction de la détection
          setOnTg(telegramApiAvailable);
          
          if (telegramApiAvailable) {
            console.log("🚀 Application lancée dans Telegram WebApp");
            
            // Initialiser l'application Telegram
            if (typeof window.Telegram.WebApp.ready === 'function') {
              window.Telegram.WebApp.ready();
            }
            
            if (typeof window.Telegram.WebApp.expand === 'function') {
              window.Telegram.WebApp.expand();
            }
            
            // STRICTEMENT les utilisateurs Telegram uniquement
            if (window.Telegram.WebApp.initDataUnsafe && window.Telegram.WebApp.initDataUnsafe.user) {
              // Si les données utilisateur sont disponibles, les utiliser
              user = window.Telegram.WebApp.initDataUnsafe.user;
              console.log("👤 Utilisateur Telegram récupéré:", user);
              
              // Sauvegarder l'état dans le localStorage pour persister entre rechargements
              localStorage.setItem('telegramUser', JSON.stringify(user));
              localStorage.setItem('inTelegram', 'true');
            } else {
              // Vérifier si on a des données dans le localStorage
              const savedUser = localStorage.getItem('telegramUser');
              const savedInTelegram = localStorage.getItem('inTelegram');
              
              if (savedUser && savedInTelegram === 'true') {
                console.log("Utilisation des données Telegram sauvegardées");
                user = JSON.parse(savedUser);
              } else {
                // Si nous sommes dans Telegram mais sans données utilisateur
                console.warn("⚠️ Données utilisateur Telegram manquantes");
                setOnTg(false);
                setSplashError("Veuillez ouvrir l'application directement depuis Telegram");
                throw new Error("Données utilisateur Telegram manquantes");
              }
            }
          } else {
            // Vérifier si on a des données dans le localStorage
            const savedUser = localStorage.getItem('telegramUser');
            const savedInTelegram = localStorage.getItem('inTelegram');
            
            if (savedUser && savedInTelegram === 'true') {
              console.log("📱 Application hors Telegram mais avec données sauvegardées");
              user = JSON.parse(savedUser);
              // On permet de continuer même sans Telegram
            } else {
              // Hors de Telegram - rediriger vers TelegramRequiredScreen
              console.log("📱 Application lancée hors environnement Telegram");
              setSplashError("Veuillez ouvrir l'application dans Telegram");
              throw new Error("Application lancée hors environnement Telegram");
            }
          }
        } catch (error) {
          console.error("❌ Détection Telegram - Erreur:", error.message);
          // Forcer l'état à false pour assurer la redirection vers TelegramRequiredScreen
          setOnTg(false);
          // Ne pas définir d'utilisateur par défaut - rediriger vers TelegramRequiredScreen
          setSplashError("Veuillez rafraîchir la page et ouvrir l'application depuis Telegram");
        }
        
        // 2. Définir l'ID utilisateur
        if (!user || !user.id) {
          throw new Error("Impossible d'identifier l'utilisateur");
        }
        
        console.log("ID utilisateur défini:", user.id);
        setId(user.id);
        
        // 3. Enregistrer l'utilisateur si nécessaire
        console.log("Vérification/enregistrement de l'utilisateur...");
        const userRegistration = await checkAndInsertData(user.id, user.username || "user");
        
        if (!userRegistration.success) {
          throw new Error(`Erreur d'enregistrement: ${userRegistration.error}`);
        }
        
        if (userRegistration.isNewUser) {
          console.log("Nouvel utilisateur enregistré:", user.id);
          
          // Garder le splash visible plus longtemps pour les nouveaux utilisateurs
          // en utilisant un minuteur personnalisé
          setTimeout(() => {
            setSplashVisible(false);
          }, 2500); // 2.5 secondes pour les nouveaux utilisateurs
          
          // Attribuer automatiquement le plan gratuit au nouvel utilisateur
          try {
            console.log("Attribution automatique du plan gratuit au nouvel utilisateur");
            const updateData = {
              plan: true,          // Indique que l'utilisateur a choisi un plan
              type_plan: "gratuit", // Attribution du plan gratuit
              deja: true,          // Indique que l'utilisateur a déjà passé l'onboarding
              updated_at: new Date().toISOString()
            };
            
            // Utiliser l'import direct défini en haut du fichier
            await updateVendeurInfo(user.id, updateData);
            
            console.log("✅ Plan gratuit attribué automatiquement à l'utilisateur:", user.id);
            
            // Nettoyer le sessionStorage pour éviter des problèmes de redirection
            sessionStorage.removeItem('initialRedirectionDone');
            
            // Marquer que le plan a été choisi (pour référence future)
            localStorage.setItem('planChosen', 'gratuit');
            localStorage.setItem('planChosenTime', new Date().toISOString());
          } catch (error) {
            console.error("❌ Erreur lors de l'attribution automatique du plan gratuit:", error);
            // Continuer malgré l'erreur - l'utilisateur pourra toujours choisir manuellement
          }
          
          // Nous avons déjà défini un délai personnalisé pour les nouveaux utilisateurs,
          // donc on empêche l'exécution du délai par défaut
          return;
        }
        
        // 4. Charger les données initiales
        console.log("Chargement des données initiales...");
        const initialData = await loadInitialData(user.id);
        
        if (!initialData.success) {
          throw new Error(`Erreur de chargement des données: ${initialData.error}`);
        }
        
        console.log("Données initiales chargées avec succès");
        
        // 5. Mettre à jour tous les états avec les données récupérées
        setUserInfos(initialData.data.userData || null);
        
        // Journaliser les informations sur le plan de l'utilisateur pour faciliter le débogage
        if (initialData.data.userData) {
          console.log("Plan de l'utilisateur:", {
            plan: initialData.data.userData.plan,
            type_plan: initialData.data.userData.type_plan
          });
        }
        
        setChannels(initialData.data.userChannels || {});
        setUserProducts(initialData.data.userProducts || []);
        setAllAggregators(initialData.data.allAggregators || null);
        setUserActiveAggregators(initialData.data.userAggregators || null);
        setAdminActiveAggregators(initialData.data.adminAggregators || null);
        
        if (initialData.data.userSubscriptions && initialData.data.userSubscriptions.length > 0) {
          setAbonnementProduit(initialData.data.userSubscriptions);
        }
        
        // 6. Rediriger vers la page de configuration de paiement au lieu d'afficher une modal
        if (initialData.data.userAggregators && 
            initialData.data.userData && 
            Object.keys(initialData.data.userAggregators).length === 0 && 
            initialData.data.userData.type_plan !== "gratuit") {
          // On utilisera la redirection pour éviter l'erreur de constructeur
          setTimeout(() => {
            navigate('/ConfigurerPaiement');
          }, 1500);
        }
        
        // 7. Mettre en place l'écouteur pour les canaux
        setupChannelsListener(user.id);
      } catch (error) {
        console.error("Erreur d'initialisation:", error);
        // En cas d'erreur, utiliser un utilisateur par défaut
        const fallbackUserId = 123456789;
        setId(fallbackUserId);
        setOnTg(false);
        
        // Afficher l'erreur à l'utilisateur dans le splash screen
        setSplashError(error.message || "Erreur de chargement");
        
        // Ne pas masquer le splash screen en cas d'erreur pour permettre
        // à l'utilisateur de voir le message d'erreur et le bouton pour réessayer
      } finally {
        // Marquer les données comme chargées et arrêter l'indicateur de chargement
        dataLoaded.current = true;
        setIsLoading(false);
        
        // TOUJOURS masquer le splash screen, même s'il y a une erreur
        console.log("Initialisation terminée, masquage du splash screen");
        setSplashVisible(false);
      }
    };
    
    init();
  }, []);
  
  // Mettre en place l'écouteur pour les canaux quand l'ID utilisateur change
  useEffect(() => {
    if (!id) return;
    
    const unsubscribe = setupChannelsListener(id);
    
    // Nettoyer l'écouteur lors du démontage du composant
    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, [id]);
  
  // Gestion des changements de route - SEULE SOURCE DE MISE À JOUR
  useEffect(() => {
    if (currentPath.current !== location.pathname) {
      currentPath.current = location.pathname;
      
      if (id && !isUpdating.current && 
          !BYPASS_LOADING_ROUTES.includes(location.pathname)) {
        loadUserData(id);
      }
    }
  }, [location.pathname, id, location.search]);
  
  // Gestion des redirections basée sur les données utilisateur
  useEffect(() => {
    // Ne rien faire si les infos utilisateur ne sont pas disponibles
    if (!userInfos) {
      console.log("Données utilisateur non disponibles pour la redirection");
      return;
    }
    
    // Si la redirection a déjà été effectuée dans cette session, ne pas la répéter
    if (redirectDone.current || sessionStorage.getItem('initialRedirectionDone')) {
      return;
    }
    
    // Marquer la redirection comme effectuée
    sessionStorage.setItem('initialRedirectionDone', 'true');
    redirectDone.current = true;
    
    // Afficher les informations de l'utilisateur pour le débogage
    console.log("Données utilisateur disponibles pour décision de redirection:", {
      id: id,
      deja: userInfos.deja, 
      plan: userInfos.plan,
      type_plan: userInfos.type_plan,
      path: location.pathname
    });
    
    // PRIORITÉ 1: Si l'utilisateur a déjà un plan, on l'envoie directement à l'espace principal
    // Peu importe si deja=false, s'il a un plan, il a déjà passé l'essentiel du processus d'onboarding
    if (userInfos.plan === true && (location.pathname === '/' || location.pathname === '/Plans' || location.pathname === '/stories')) {
      console.log("REDIRECTION PRIORITAIRE: Utilisateur avec plan existant -> Espace principal");
      
      // Mise à jour silencieuse pour corriger deja=false si nécessaire
      if (userInfos.deja === false) {
        try {
          // Utiliser un timeout pour ne pas bloquer la navigation
          setTimeout(async () => {
            console.log("Correction automatique: Mise à jour de deja=true pour utilisateur avec plan");
            const updateUserRef = ref(database, `vendeur/${id}`);
            await set(updateUserRef, { ...userInfos, deja: true });
          }, 500);
        } catch (err) {
          console.warn("Erreur lors de la correction deja:", err);
        }
      }
      
      navigate('/IndexEspace');
      return;
    }
    
    // Tous les nouveaux utilisateurs ont maintenant automatiquement un plan gratuit
    // Donc les règles suivantes ne devraient s'appliquer que dans des cas exceptionnels
    
    // RÈGLE 2: Première visite -> Stories (onboarding)
    // Si l'utilisateur n'a jamais vu les stories (deja = false)
    if (userInfos.deja === false && location.pathname !== '/stories') {
      console.log("REDIRECTION: Premier démarrage -> Stories");
      navigate('/stories');
      return;
    }
    
    // RÈGLE 3: Pas de plan encore choisi -> Page Plans
    // Si l'utilisateur a vu les stories mais n'a pas encore choisi de plan (plan = false)
    if (userInfos.deja === true && userInfos.plan === false && location.pathname !== '/Plans') {
      console.log("REDIRECTION: Plan non choisi -> Plans");
      navigate('/Plans');
      return;
    }
    
    console.log("Aucune redirection nécessaire selon les règles");
  }, [userInfos, id, location.pathname, navigate]);
  
  // Gestion du bouton retour de Telegram - VERSION SIMPLIFIÉE
  const setupTelegramBackButton = useCallback((show = true, callback = null) => {
    try {
      // Vérifier si l'API de bouton retour est disponible
      if (window.Telegram && window.Telegram.WebApp && window.Telegram.WebApp.BackButton) {
        const backButton = window.Telegram.WebApp.BackButton;
        
        // Afficher ou masquer le bouton
        if (show) {
          if (typeof backButton.show === 'function') {
            backButton.show();
          }
          
          // Attacher le callback si fourni
          if (callback && typeof backButton.onClick === 'function') {
            backButton.onClick(callback);
          }
        } else {
          if (typeof backButton.hide === 'function') {
            backButton.hide();
          }
        }
        
        return true;
      } 
      // Si l'API n'est pas disponible mais qu'un callback est fourni
      else if (callback && show) {
        // Mettre en place un bouton de retour personnalisé
        const backCallbackId = `back_callback_${Date.now()}`;
        
        // Stocker le callback pour le bouton retour du navigateur
        window[backCallbackId] = callback;
        
        // Écouter le bouton retour du navigateur
        window.addEventListener('popstate', function handlePopState(event) {
          if (window[backCallbackId]) {
            window[backCallbackId]();
            // Nettoyage
            window.removeEventListener('popstate', handlePopState);
            delete window[backCallbackId];
          }
        });
        
        console.log("Bouton retour personnalisé mis en place (API Telegram non disponible)");
        return true;
      } else {
        console.log("Bouton retour Telegram non disponible");
        return false;
      }
    } catch (error) {
      console.error("Erreur lors de la gestion du bouton retour:", error);
      
      // En cas d'erreur, essayer quand même de mettre en place le callback si fourni
      if (callback && show) {
        try {
          const backCallbackId = `back_callback_error_${Date.now()}`;
          window[backCallbackId] = callback;
          
          window.addEventListener('popstate', function handlePopState(event) {
            if (window[backCallbackId]) {
              window[backCallbackId]();
              window.removeEventListener('popstate', handlePopState);
              delete window[backCallbackId];
            }
          });
          
          return true;
        } catch (secondError) {
          console.error("Double erreur lors de la gestion du bouton retour:", secondError);
        }
      }
      
      return false;
    }
  }, []);
  
  // Valeur mémorisée du contexte
  // Force le masquage du splash screen après un délai maximum
  useEffect(() => {
    if (isSplashVisible) {
      const forceTimeout = setTimeout(() => {
        console.log("⚠️ Masquage forcé du splash screen après timeout");
        setSplashVisible(false);
      }, 5000); // 5 secondes maximum
      
      // Exposer une méthode pour masquer le splash screen
      window.__APP_CONTEXT = {
        ...window.__APP_CONTEXT,
        hideSplash: () => setSplashVisible(false)
      };
      
      // Vérifier si le localStorage indique que le splash doit être masqué
      if (localStorage.getItem('splashHidden') === 'true') {
        setSplashVisible(false);
      }
      
      return () => {
        clearTimeout(forceTimeout);
        if (window.__APP_CONTEXT) {
          delete window.__APP_CONTEXT.hideSplash;
        }
      };
    }
  }, [isSplashVisible, setSplashVisible]);
  
  const contextValue = useMemo(() => ({
    id,
    isSplashVisible,
    setSplashVisible,
    ontg,
    isLoading,
    splashError,
    setSplashError,
    userInfos, 
    setUserInfos,
    channels, 
    setChannels,
    showPaymentConfig,
    setShowPaymentConfig,
    showPaymentSheet,
    setShowPaymentSheet,
    userProducts, 
    setUserProducts,
    unProduit, 
    setUnProduit,
    allAggregators,
    setAllAggregators,
    userActiveAggregators,
    setUserActiveAggregators,
    method, 
    setMethod,
    abonnementProduit, 
    setAbonnementProduit,
    adminActiveAggregators, 
    setAdminActiveAggregators,
    setupTelegramBackButton // Ajout de la fonction pour configurer le bouton retour
  }), [
    id, isSplashVisible, ontg, isLoading, splashError, userInfos, channels, 
    showPaymentConfig, showPaymentSheet, userProducts, unProduit, allAggregators, 
    userActiveAggregators, method, abonnementProduit, adminActiveAggregators,
    setupTelegramBackButton
  ]);
  
  // Nous utilisons maintenant seulement le SplashScreen unifié, 
  // donc aucun affichage spécial n'est nécessaire ici

  return (
    <AppContext.Provider value={contextValue}>
      {children}
    </AppContext.Provider>
  );
};

export const useApp = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useApp must be used within an AppProvider');
  }
  return context;
};

export default AppContext;